import type { CSSProperties } from 'react';

/**
 * Hand-drawn SVG accents, ported from
 * claude-design/orborix-speak/project/doodles.jsx.
 *
 * Typed primitives (props via interface), no `Object.assign(window, ...)`.
 * Keep usage to max ~2 static doodles per screen (see 00-overview.md).
 */

interface UnderlineProps {
  color?: string;
  width?: number;
  style?: CSSProperties;
}

export function Underline({ color = '#d4f23e', width = 160, style = {} }: UnderlineProps) {
  return (
    <svg
      viewBox="0 0 160 14"
      width={width}
      height={(14 * width) / 160}
      style={{ display: 'block', ...style }}
    >
      <path
        d="M3 9 C 25 4, 55 2, 80 5 S 130 11, 156 6"
        stroke={color}
        strokeWidth="3"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface ShortUnderlineProps {
  color?: string;
  width?: number;
  style?: CSSProperties;
}

export function ShortUnderline({ color = '#a78bfa', width = 80, style = {} }: ShortUnderlineProps) {
  return (
    <svg
      viewBox="0 0 80 10"
      width={width}
      height={(10 * width) / 80}
      style={{ display: 'block', ...style }}
    >
      <path
        d="M2 6 C 15 2, 35 1, 50 4 S 70 8, 78 5"
        stroke={color}
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface BrushStrokeProps {
  color?: string;
  height?: number;
}

export function BrushStroke({ color = '#d4f23e', height = 90 }: BrushStrokeProps) {
  return (
    <svg viewBox="0 0 14 90" width="14" height={height} style={{ display: 'block' }}>
      <path
        d="M9 6 C 4 22, 3 45, 6 80"
        stroke={color}
        strokeWidth="4"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface SparkleProps {
  color?: string;
  size?: number;
  style?: CSSProperties;
}

export function Sparkle({ color = '#d4f23e', size = 16, style = {} }: SparkleProps) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} style={style}>
      <path d="M12 2 L13.5 9.5 L21 11 L13.5 12.5 L12 20 L10.5 12.5 L3 11 L10.5 9.5 Z" fill={color} />
    </svg>
  );
}

interface SparkleOutlineProps {
  color?: string;
  size?: number;
  style?: CSSProperties;
}

export function SparkleOutline({ color = '#a78bfa', size = 24, style = {} }: SparkleOutlineProps) {
  return (
    <svg viewBox="0 0 28 28" width={size} height={size} style={style}>
      <g stroke={color} strokeWidth="1.8" fill="none" strokeLinecap="round">
        <path d="M14 5 V 11" />
        <path d="M14 17 V 23" />
        <path d="M5 14 H 11" />
        <path d="M17 14 H 23" />
        <path d="M7.5 7.5 L 10.5 10.5" />
        <path d="M17.5 17.5 L 20.5 20.5" />
        <path d="M7.5 20.5 L 10.5 17.5" />
        <path d="M17.5 10.5 L 20.5 7.5" />
      </g>
    </svg>
  );
}

interface TitleSparksProps {
  color?: string;
  size?: number;
  style?: CSSProperties;
}

export function TitleSparks({ color = '#d4f23e', size = 22, style = {} }: TitleSparksProps) {
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} style={style}>
      <g stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round">
        <path d="M14 3 L 16 9" />
        <path d="M22 5 L 19 11" />
        <path d="M28 11 L 22 14" />
      </g>
    </svg>
  );
}

interface WaveformProps {
  color?: string;
  size?: number;
}

export function Waveform({ color = '#d4f23e', size = 20 }: WaveformProps) {
  return (
    <svg viewBox="0 0 24 16" width={size} height={(size * 16) / 24} style={{ display: 'block' }}>
      <g stroke={color} strokeWidth="2" strokeLinecap="round">
        <line x1="2" y1="8" x2="2" y2="8" />
        <line x1="5" y1="4" x2="5" y2="12" />
        <line x1="8" y1="6" x2="8" y2="10" />
        <line x1="11" y1="2" x2="11" y2="14" />
        <line x1="14" y1="5" x2="14" y2="11" />
        <line x1="17" y1="3" x2="17" y2="13" />
        <line x1="20" y1="7" x2="20" y2="9" />
        <line x1="23" y1="8" x2="23" y2="8" />
      </g>
    </svg>
  );
}

interface FlagProps {
  size?: number;
  style?: CSSProperties;
}

export function Flag({ size = 26, style = {} }: FlagProps) {
  return (
    <svg viewBox="0 0 32 32" width={size} height={size} style={style}>
      <line x1="6" y1="4" x2="6" y2="28" stroke="#e9eaf2" strokeWidth="2" strokeLinecap="round" />
      <g>
        <rect x="8" y="6" width="5" height="5" fill="#e9eaf2" />
        <rect x="13" y="6" width="5" height="5" fill="#0c0d15" />
        <rect x="18" y="6" width="5" height="5" fill="#e9eaf2" />
        <rect x="8" y="11" width="5" height="5" fill="#0c0d15" />
        <rect x="13" y="11" width="5" height="5" fill="#e9eaf2" />
        <rect x="18" y="11" width="5" height="5" fill="#0c0d15" />
        <rect x="8" y="16" width="5" height="5" fill="#e9eaf2" />
        <rect x="13" y="16" width="5" height="5" fill="#0c0d15" />
        <rect x="18" y="16" width="5" height="5" fill="#e9eaf2" />
      </g>
      <rect
        x="8"
        y="6"
        width="15"
        height="15"
        fill="none"
        stroke="#e9eaf2"
        strokeWidth="0.5"
        opacity="0.4"
      />
    </svg>
  );
}

interface SquigglyUnderlineProps {
  color?: string;
  width?: number;
}

/** Sketchy curvy "underline that wraps around a word". */
export function SquigglyUnderline({ color = '#a78bfa', width = 70 }: SquigglyUnderlineProps) {
  return (
    <svg viewBox="0 0 70 8" width={width} height={(8 * width) / 70} style={{ display: 'block' }}>
      <path
        d="M2 5 Q 12 1, 22 4 T 42 4 T 68 4"
        stroke={color}
        strokeWidth="2.2"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

interface EmphasisProps {
  color?: string;
  size?: number;
  style?: CSSProperties;
}

/** Little tick marks like motion / emphasis decorations next to a button. */
export function Emphasis({ color = '#a78bfa', size = 30, style = {} }: EmphasisProps) {
  return (
    <svg viewBox="0 0 30 30" width={size} height={size} style={style}>
      <g stroke={color} strokeWidth="2.2" strokeLinecap="round" fill="none">
        <path d="M22 4 L 26 1" />
        <path d="M24 11 L 29 11" />
        <path d="M19 7 L 21 10" />
      </g>
    </svg>
  );
}
